import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type { TodoListLinkResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useToast } from '@/components/ui/use-toast';

interface UseTodoListLinksReturn {
  links: TodoListLinkResponse[];
  loading: boolean;
  createLink: (input: {
    targetListId: string;
    defaultTargetSectionId?: string | null;
  }) => Promise<TodoListLinkResponse>;
  updateLink: (
    linkId: string,
    input: { defaultTargetSectionId?: string | null; sortOrder?: number },
  ) => Promise<TodoListLinkResponse>;
  deleteLink: (linkId: string) => Promise<void>;
}

/**
 * A source list's outbound cross-list move shortcuts. CRUD is owner/admin
 * server-side; any member can read (to render the "Move to list…" targets).
 * The link is a curated shortcut + optional default landing section — not a
 * permission gate (moving is owner/admin regardless).
 */
export function useTodoListLinks(holderId: string | null, listId: string): UseTodoListLinksReturn {
  const queryClient = useQueryClient();
  const key = queryKeys.todoLists.links(holderId ?? '', listId);
  const { toast } = useToast();

  const onError = () => toast({ title: 'Something went wrong', variant: 'destructive' });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: key });

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api.listTodoListLinks(holderId as string, listId),
    enabled: !!holderId && !!listId,
  });

  const createMut = useMutation({
    mutationFn: (input: { targetListId: string; defaultTargetSectionId?: string | null }) =>
      api.createTodoListLink(holderId as string, listId, input).then((r) => r.link),
    onSuccess: invalidate,
    onError,
  });
  const updateMut = useMutation({
    mutationFn: (vars: {
      linkId: string;
      input: { defaultTargetSectionId?: string | null; sortOrder?: number };
    }) => api.updateTodoListLink(holderId as string, vars.linkId, vars.input).then((r) => r.link),
    onSuccess: invalidate,
    onError,
  });
  const deleteMut = useMutation({
    mutationFn: (linkId: string) => api.deleteTodoListLink(holderId as string, linkId),
    onSuccess: invalidate,
    onError,
  });

  return {
    links: data?.links ?? [],
    loading: isLoading,
    createLink: (input) => createMut.mutateAsync(input),
    updateLink: (linkId, input) => updateMut.mutateAsync({ linkId, input }),
    deleteLink: (linkId) => deleteMut.mutateAsync(linkId),
  };
}
